import React from "react"
import { IntlShape, useIntl } from "react-intl"
import { ReactComponent as ArrowLeft } from "assets/icons/arrow-left-negative-amount.svg"
import { ReactComponent as ArrowRight } from "assets/icons/arrow-right-positive-amount.svg"
import "./index.scss"
import { Popover } from "@walmart-web/livingdesign-components"
import IconInformation from "assets/icons/iconInformation.svg"
import { CHILEAN_CURRENCY, formatNumber, isNumber } from "helpers/numbers"
import { CardTreeProps, NodeDatum } from "./interfaces"
import { generateTitleFatherNode, validateIsFatherNode } from "./utils"
import { INDEX_ONE, INDEX_TWO, INDEX_ZERO, NO_DATA, ZERO } from "./constants"
import { concatClassNames } from "helpers/jsxHelpers"

const validateValue = (index: number) =>
	index === INDEX_ZERO ? (
		<ArrowRight className="arrow positive" />
	) : (
		<ArrowLeft className="arrow negative" />
	)

const generateFormatValue = (value: string, intl: IntlShape) => {
	return !isNumber(value)
		? NO_DATA
		: `${CHILEAN_CURRENCY} ${formatNumber(Number(value), intl)}`
}

const CardTree: React.FC<CardTreeProps> = ({ nodeDatum }) => {
	const buttonRef = React.useRef<HTMLButtonElement>(null)
	const [isOpen, setIsOpen] = React.useState(false)
	const intl = useIntl()
	const renderTooltip = () => {
		return (
			<div className="container-tooltip">
				<Popover
					content={
						<div className="description-tooltip">
							{intl.formatMessage({
								id: nodeDatum.informationTooltip,
							})}
						</div>
					}
					onClose={() => setIsOpen(isOpen)}
					isOpen={isOpen}
					onMouseOut={() => setIsOpen(false)}
					position="top"
					triggerRef={buttonRef}
					hasNubbin
				>
					<div onMouseOver={() => setIsOpen(true)}>
						<img className="info-button-card-tree" src={IconInformation}></img>
					</div>
				</Popover>
			</div>
		)
	}
	const isGreenOrRedTitle = (nodeDatum: NodeDatum) => {
		if (Number(nodeDatum.informationNode[INDEX_TWO].value) < ZERO) {
			return "red-color-title"
		}
		return "green-color-title"
	}
	const isGreenOrRedDescription = (index: number, value: string) => {
		if (index === INDEX_TWO) {
			return (
				<p
					id={`value-${index}`}
					className={Number(value) < ZERO ? "red-color" : "green-color"}
				>
					{generateFormatValue(value, intl)}
				</p>
			)
		}
		return <p id={`value-${index}`}>{generateFormatValue(value, intl)}</p>
	}
	const renderTitleCard = () => {
		const titleNode = validateIsFatherNode(nodeDatum.id)
			? generateTitleFatherNode(nodeDatum.textCardFather)
			: intl.formatMessage({ id: nodeDatum.name })
		return (
			<div
				className={concatClassNames([
					"title-card",
					isGreenOrRedTitle(nodeDatum),
				])}
			>
				<p>{titleNode}</p>
				{nodeDatum.informationTooltip && renderTooltip()}
			</div>
		)
	}

	const renderDetailCard = () => {
		return (
			<div className="container-card">
				{nodeDatum.informationNode.map(({ description, value }, index) => (
					<div key={`information-card-${index}`}>
						<div
							className="flexbox-container"
							id={`flexbox-container-${index}`}
						>
							<p id={`description-${index}`}>
								{intl.formatMessage({
									id: description,
								})}
							</p>
							{index !== INDEX_TWO && validateValue(index)}
							{isGreenOrRedDescription(index, value)}
						</div>
						{index === INDEX_ONE && <hr></hr>}
					</div>
				))}
			</div>
		)
	}

	return (
		<>
			{nodeDatum.informationTooltip && <div className="container-space"></div>}
			<div
				className="container-father"
				id={nodeDatum.id}
				data-testid={`card-tree-component-${nodeDatum.id}`}
			>
				{renderTitleCard()}
				{renderDetailCard()}
			</div>
		</>
	)
}

export default CardTree
